import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  memberName: PropTypes.string,
  message: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar name={value} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {value}
        </MDTypography>
        <MDTypography variant="caption">{church?.email || "church@example.com"}</MDTypography>
      </MDBox>
    </MDBox>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const MessageCell = ({ value, row }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="button" fontWeight="medium">
      {value}
    </MDTypography>
    <MDTypography variant="caption" color="text">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </MDTypography>
  </MDBox>
);
MessageCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "sent" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useMessageData() {
  const { user, church } = useAuth();
  const ChurchCell = createChurchCell(church);

  const [messageData, setMessageData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Member",
        accessor: "memberName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Message",
        accessor: "message",
        align: "left",
        width: "25%",
        Cell: MessageCell,
      },
      {
        Header: "Type",
        accessor: "type",
        align: "left",
        width: "25%",
        Cell: MessageCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [members, setMembers] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleEdit = (message) => {
    setSelectedMessage(message);
    setEditModalOpen(true);
  };

  const handleDelete = (message) => {
    setSelectedMessage(message);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async (messageData) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = messageData.id
        ? `https://api.ofi-cia.com/api/sms/send/${messageData.id}`
        : "https://api.ofi-cia.com/api/sms/send";

      const method = messageData.id ? "PUT" : "POST";

      // Ensure required fields are present
      if (!messageData.recipient) {
        throw new Error("Recipient phone number is required");
      }

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: messageData.member_id,
          recipient: messageData.recipient,
          message: messageData.message,
          type: messageData.type,
          status: "pending",
          member_id: messageData.member_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Message successfully ${messageData.id ? "updated" : "sent"}`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchMessages();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Message operation failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const sendBulkMessages = async (memberIds, message, type) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      // Get phone numbers for selected members
      const recipients = members
        .filter((member) => memberIds.includes(member.id))
        .map((member) => member.phone);

      if (recipients.length === 0) {
        throw new Error("No valid recipients found");
      }

      const response = await fetch("https://api.ofi-cia.com/api/sms/send-bulk", {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipients, // Send array of phone numbers
          message,
          type: type || "bulk",
          status: "pending",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Messages sent successfully to ${recipients.length} recipients`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchMessages();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Bulk message sending failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/messages/${selectedMessage.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Message deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchMessages();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleAddMessage = () => {
    setSelectedMessage({
      id: null,
      recipient: "",
      message: "",
      type: "sms",
      status: "pending",
      member_id: null,
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/members/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setMembers(result.data || result.members || result);
    } catch (error) {
      console.error("Fetch members failed:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      setMessageData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/sms/members/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const messages = result.data || result.messages || result;

      if (!Array.isArray(messages)) {
        throw new Error("API response is not an array");
      }

      setMessageData((prev) => ({
        ...prev,
        rows: messages.map((message, index) => ({
          id: message.id,
          serial: index + 1,
          memberName: message.member
            ? `${message.member.first_name} ${message.member.surname}`
            : "N/A",
          message: message.message,
          type: message.type,
          status: message.status || "pending",
          createdAt: message.created_at || message.createdAt,
          originalData: message,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch messages failed:", error);
      setMessageData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchMembers();
  }, [user, church]);

  return {
    ...messageData,
    members,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMessage,
    setSelectedMessage,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMessage,
    sendBulkMessages,
  };
}
